import { getOrCreateProgram } from '../glContext.js';
import {
  SIM_VERTEX_SOURCE,
  bindTexture,
  blitTo,
  createSimDoubleTarget,
  disposeSimDoubleTarget,
  resolveSimFormats,
  setupSimQuad,
  type SimDoubleTarget,
} from './simGl.js';
import type { FluidStepInputs } from './fluidSim.js';
import { EnergyParticles, ENERGY_PARTICLE_LIMIT } from './energyParticles.js';

export type WaveVariantId =
  | 'wave-sunset-light'
  | 'wave-sunset-dark'
  | 'wave-bloom-light'
  | 'wave-bloom-dark';

export const WAVE_STEP_SECONDS = 1 / 120;
export const WAVE_STIFFNESS = 2600;
export const WAVE_DAMPING = 1.8;
export function waveSubsteps(dt: number): number {
  return Math.max(1, Math.min(6, Math.ceil(Math.max(0, dt) / WAVE_STEP_SECONDS)));
}

// Height/spring velocity in RG, advected surface flow in BA. Bounded
// semi-Lagrangian flow carries a finger's wake instead of only making rings.
const STEP = `#version 300 es
precision highp float;
uniform sampler2D uState;
uniform vec2 uTexel, uPointer, uStroke;
uniform float uTouch, uTime, uIntensity, uDt, uBloom;
in vec2 vUv;
out vec4 frag;
void main() {
  vec4 prior=texture(uState,vUv);
  vec2 flow=prior.ba;
  vec2 back=clamp(vUv-flow*uDt,vec2(0.001),vec2(0.999));
  vec4 s=texture(uState,back);
  float l=texture(uState,back-vec2(uTexel.x,0)).r;
  float r=texture(uState,back+vec2(uTexel.x,0)).r;
  float b=texture(uState,back-vec2(0,uTexel.y)).r;
  float t=texture(uState,back+vec2(0,uTexel.y)).r;
  float lap=l+r+b+t-4.0*s.r;
  vec2 delta=vUv-uPointer;
  float brush=exp(-dot(delta,delta)/0.0038)*uTouch;
  float wake=dot(delta,uStroke)*brush*250.0;
  vec2 source=mix(vec2(0.5+0.22*sin(uTime*0.65),0.32),
    vec2(0.5+0.08*sin(uTime),0.56+0.09*sin(uTime*0.4)),uBloom);
  float charge=exp(-dot(vUv-source,vUv-source)/0.0015)
    *sin(uTime*3.5)*(3.0+uIntensity*6.0);
  float velocity=(s.g+(lap*${WAVE_STIFFNESS.toFixed(1)}-s.r*2.5
    +brush*55.0+wake+charge)*uDt)*exp(-${WAVE_DAMPING.toFixed(1)}*uDt);
  float height=s.r+velocity*uDt;
  flow=s.ba*exp(-uDt*1.7)+brush*uStroke*uDt*14.0;
  flow+=brush*vec2(-delta.y,delta.x)*length(uStroke)*uDt*32.0;
  flow-=vec2(r-l,t-b)*uDt*3.5;
  float edge=min(min(vUv.x,1.0-vUv.x),min(vUv.y,1.0-vUv.y));
  float boundary=smoothstep(0.0,0.025,edge);
  frag=vec4(clamp(height,-2.0,2.0)*boundary,clamp(velocity,-45.0,45.0)*boundary,
    clamp(flow,vec2(-1.2),vec2(1.2))*boundary);
}`;

const DISPLAY = `#version 300 es
precision highp float;
uniform sampler2D uState;
uniform vec2 uTexel, uPointer;
uniform float uTime, uBloom, uDark, uIntensity, uGesture, uHold;
in vec2 vUv;
out vec4 frag;
vec3 wave(vec2 p) {
  // Height and its analytic gradient share the same four waves. This avoids
  // evaluating the complete surface five times for every phone-screen pixel.
  vec2 warpPhase=vec2(p.y*2.4+uTime*0.3,p.x*3.2-uTime*0.2);
  p+=vec2(sin(warpPhase.x),cos(warpPhase.y))*0.12;
  vec4 phase=vec4(dot(p,vec2(4.5,7.0))-uTime*0.7,
    dot(p,vec2(-8.5,11.0))+uTime*0.85,
    dot(p,vec2(16.0,19.0))-uTime*1.25,
    dot(p,vec2(-24.0,28.0))+uTime*1.45);
  vec4 amplitude=vec4(0.10,0.047,0.019,0.008);
  vec4 derivative=cos(phase)*amplitude;
  vec2 gradient=vec2(dot(derivative,vec4(4.5,-8.5,16.0,-24.0)),
    dot(derivative,vec4(7.0,11.0,19.0,28.0)));
  gradient=vec2(gradient.x-gradient.y*sin(warpPhase.y)*0.384,
    gradient.y+gradient.x*cos(warpPhase.x)*0.288);
  return vec3(dot(sin(phase),amplitude),gradient);
}
float flower(vec2 p,float radius,float petals,float time) {
  float a=atan(p.y,p.x),r=length(p);
  float lobe=pow(0.5+0.5*cos(a*petals+time),0.7);
  float edge=radius*(0.48+0.52*lobe);
  return (1.0-smoothstep(edge-0.003,edge+0.004,r))
    *smoothstep(radius*0.12,radius*0.38,r);
}
void main() {
  vec4 state=texture(uState,vUv);
  float h=state.r;
  vec2 slope=vec2(texture(uState,vUv+vec2(uTexel.x,0)).r-h,
    texture(uState,vUv+vec2(0,uTexel.y)).r-h)*8.0;
  vec2 uv=vUv+slope*0.026;
  vec3 sky=mix(vec3(1.0,0.83,0.68),vec3(0.11,0.035,0.18),uDark);
  vec3 warm=mix(vec3(1.0,0.48,0.32),vec3(0.9,0.24,0.38),uDark);
  vec3 col=mix(warm,sky,smoothstep(0.40,1.0,uv.y));
  if(uBloom>0.5) {
    // A living solar flower. The held blossom opens where the finger is,
    // while its petals continue as separately simulated drifting particles.
    vec2 sun=vec2(0.5+0.018*sin(uTime*0.2),0.56+0.025*sin(uTime*0.35));
    sun=mix(sun,uPointer,0.08*uGesture);
    vec2 p=uv-sun;
    float r=length(p),a=atan(p.y,p.x);
    float radius=0.21+0.012*sin(uTime*1.4)+h*0.015;
    float petals=flower(p,radius,8.0,uTime*0.15);
    float innerPetals=flower(p,radius*0.77,8.0,-uTime*0.12+0.5);
    col=mix(vec3(1.0,0.62,0.52),vec3(1.0,0.91,0.77),uv.y)*(1.0-uDark)
      +mix(vec3(0.39,0.065,0.20),vec3(0.075,0.035,0.17),uv.y)*uDark;
    col+=vec3(1.0,0.35,0.17)*exp(-r*4.0)*0.32;
    vec3 petalColor=mix(vec3(1.0,0.54,0.37),vec3(1.0,0.42,0.55),0.5+0.5*sin(a*3.0));
    col=mix(col,petalColor,petals*0.88);
    col=mix(col,vec3(1.0,0.71,0.33),innerPetals*0.55);
    float ridge=pow(0.5+0.5*cos(a*8.0+uTime*0.15),3.0)*exp(-r*7.0);
    col+=vec3(1.0,0.65,0.21)*ridge*0.32;
    float glow=exp(-r*9.0)*(0.27+uIntensity*0.24);
    col+=vec3(1.0,0.59,0.16)*glow;
    float disk=1.0-smoothstep(0.062,0.071,r);
    col=mix(col,vec3(1.0,0.90,0.46),disk);
    vec2 touch=uv-uPointer;
    float grow=1.0-exp(-uHold*1.5);
    float touchRadius=0.065+grow*0.17;
    float touchPetals=flower(touch,touchRadius,9.0,-uTime*0.28);
    col=mix(col,mix(vec3(1.0,0.73,0.34),vec3(1.0,0.43,0.60),
      0.5+0.5*sin(atan(touch.y,touch.x)*2.0)),touchPetals*uGesture*0.83);
    col+=vec3(1.0,0.63,0.22)*exp(-length(touch)*16.0)*uGesture*0.32;
    col=mix(col,vec3(1.0,0.94,0.65),
      (1.0-smoothstep(0.015,0.032,length(touch)))*uGesture);
  } else {
    // Perspective water: choppy overlapping swell, shaded wave faces and
    // broken sunlight. Surface flow bends the highlights into a finger's wake.
    float horizon=0.49+0.005*sin(uv.x*8.0+uTime*0.2);
    vec2 sun=vec2(0.52+0.015*sin(uTime*0.15),0.68);
    float sr=length(uv-sun);
    col+=vec3(1.0,0.53,0.19)*exp(-sr*8.0)*0.23;
    col=mix(col,vec3(1.0,0.89,0.52),1.0-smoothstep(0.086,0.092,sr));
    float water=1.0-smoothstep(horizon-0.012,horizon+0.008,uv.y);
    float depth=max(0.0,horizon-uv.y);
    vec2 q=vec2((uv.x-0.5)/(0.28+depth),-log(0.14+depth)*1.6);
    q+=state.ba*0.24+slope*0.065;
    vec3 surface=wave(q);
    float swell=surface.x+h*0.18;
    vec2 normal2=surface.yz+slope*5.0;
    vec3 normal=normalize(vec3(-normal2.x,-normal2.y,1.65));
    float face=max(0.0,dot(normal,normalize(vec3(0.3,0.75,1.0))));
    float shine=pow(face,12.0);
    float reflectX=uv.x-sun.x+normal2.x*0.028+swell*0.24+state.b*0.07;
    float reflected=exp(-pow(reflectX/(0.032+depth*0.31),2.0));
    vec3 ocean=mix(vec3(0.20,0.29,0.34),vec3(0.035,0.055,0.13),uDark);
    vec3 sea=ocean*(0.5+face*1.05);
    sea+=vec3(0.30,0.15,0.31)*(0.2+normal.y*0.15);
    sea+=vec3(1.0,0.43,0.16)*reflected*(0.13+shine*1.28);
    sea+=vec3(1.0,0.85,0.56)*reflected*pow(face,32.0)*0.9;
    float crest=smoothstep(0.65,1.6,length(normal2));
    sea+=vec3(0.40,0.35,0.45)*crest*face*0.16;
    col=mix(col,sea,water);
  }
  col*=1.0-0.12*dot(vUv-0.5,vUv-0.5);
  frag=vec4(col,1);
}`;

const PARTICLE_VERTEX = `#version 300 es
precision highp float;
in vec4 aParticle, aLife;
uniform float uCanvas, uMaxPoint;
out vec4 vLife;
out float vAngle;
void main() {
  gl_Position=vec4(aParticle.xy*2.0-1.0,0,1);
  gl_PointSize=clamp(aParticle.z*uCanvas*(1.0+aLife.x*0.4),2.0,uMaxPoint);
  vAngle=aParticle.w; vLife=aLife;
}`;
const PARTICLE_FRAGMENT = `#version 300 es
precision highp float;
in vec4 vLife;
in float vAngle;
out vec4 frag;
void main() {
  vec2 p=gl_PointCoord-0.5;
  float c=cos(vAngle),s=sin(vAngle);
  p=mat2(c,-s,s,c)*p;
  float body=vLife.y>0.5
    ? (1.0-smoothstep(0.32,0.47,length(p*vec2(1.0,1.9))))
    : exp(-dot(p*vec2(3.0,2.2),p*vec2(3.0,2.2))*5.0);
  float halo=exp(-dot(p,p)*18.0);
  float fade=smoothstep(0.0,0.08,vLife.x)*(1.0-smoothstep(0.5,1.0,vLife.x));
  vec3 color=mix(vec3(1.0,0.87,0.44),vec3(1.0,0.52,0.63),vLife.y*vLife.z);
  frag=vec4(color,(body*0.85+halo*0.2)*fade);
}`;

export class WaveSim {
  private time = 0;
  private intensity = 0.2;
  private particles: EnergyParticles;
  private maxPoint: number;
  private constructor(
    private gl: WebGL2RenderingContext,
    private state: SimDoubleTarget,
    private quad: WebGLBuffer,
    private particleBuffer: WebGLBuffer,
    private stepProgram: WebGLProgram,
    private displayProgram: WebGLProgram,
    private particleProgram: WebGLProgram,
    private variant: WaveVariantId
  ) {
    this.particles = new EnergyParticles(variant.includes('bloom'));
    this.maxPoint = Math.min(
      64,
      (gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as Float32Array)[1] ?? 16
    );
  }

  static create(
    gl: WebGL2RenderingContext,
    variant: WaveVariantId,
    canvasSize: number
  ): WaveSim | null {
    const formats = resolveSimFormats(gl);
    if (!formats.float) return null;
    const step = getOrCreateProgram(gl, 'sim:wave:flow-step', SIM_VERTEX_SOURCE, STEP);
    const display = getOrCreateProgram(
      gl,
      'sim:wave:liquid-flower-display',
      SIM_VERTEX_SOURCE,
      DISPLAY
    );
    const particles = getOrCreateProgram(gl, 'sim:wave:petals', PARTICLE_VERTEX, PARTICLE_FRAGMENT);
    if (!step || !display || !particles) return null;
    const resolution = Math.max(64, Math.min(144, Math.round(canvasSize / 2)));
    const state = createSimDoubleTarget(gl, resolution, resolution, formats);
    const quad = gl.createBuffer(),
      particleBuffer = gl.createBuffer();
    if (!state || !quad || !particleBuffer) {
      if (state) disposeSimDoubleTarget(gl, state);
      if (quad) gl.deleteBuffer(quad);
      if (particleBuffer) gl.deleteBuffer(particleBuffer);
      return null;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );
    gl.bindBuffer(gl.ARRAY_BUFFER, particleBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, ENERGY_PARTICLE_LIMIT * 8 * 4, gl.DYNAMIC_DRAW);
    for (const target of [state.read, state.write]) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
    return new WaveSim(gl, state, quad, particleBuffer, step, display, particles, variant);
  }

  step(dt: number, inputs: FluidStepInputs): void {
    this.time = inputs.timeSec;
    this.intensity = inputs.intensity;
    this.particles.step(dt, inputs);
    const gl = this.gl,
      p = this.stepProgram;
    gl.disable(gl.BLEND);
    gl.useProgram(p);
    setupSimQuad(gl, p, this.quad);
    const n = waveSubsteps(dt),
      stepDt = Math.min(WAVE_STEP_SECONDS, Math.max(0, dt) / n);
    const bloom = this.variant.includes('bloom');
    gl.uniform2f(gl.getUniformLocation(p, 'uTexel'), ...this.state.texelSize);
    gl.uniform2f(
      gl.getUniformLocation(p, 'uPointer'),
      inputs.pointer.x,
      bloom ? inputs.pointer.y : Math.min(0.47, inputs.pointer.y)
    );
    const strokeScale = 1 / Math.max(0.016, dt);
    gl.uniform2f(
      gl.getUniformLocation(p, 'uStroke'),
      Math.max(-1.4, Math.min(1.4, inputs.pointer.dx * strokeScale)),
      Math.max(-1.4, Math.min(1.4, inputs.pointer.dy * strokeScale))
    );
    gl.uniform1f(gl.getUniformLocation(p, 'uTouch'), inputs.pointer.down ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(p, 'uIntensity'), inputs.active ? inputs.intensity : 0);
    gl.uniform1f(gl.getUniformLocation(p, 'uBloom'), bloom ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(p, 'uDt'), stepDt);
    for (let i = 0; i < n; i++) {
      gl.uniform1f(gl.getUniformLocation(p, 'uTime'), this.time + i * stepDt);
      gl.uniform1i(gl.getUniformLocation(p, 'uState'), bindTexture(gl, this.state.read.texture, 0));
      blitTo(gl, this.state.write);
      this.state.swap();
    }
  }

  render(): void {
    const gl = this.gl,
      p = this.displayProgram;
    gl.disable(gl.BLEND);
    gl.useProgram(p);
    setupSimQuad(gl, p, this.quad);
    gl.uniform1i(gl.getUniformLocation(p, 'uState'), bindTexture(gl, this.state.read.texture, 0));
    gl.uniform2f(gl.getUniformLocation(p, 'uTexel'), ...this.state.texelSize);
    gl.uniform2f(
      gl.getUniformLocation(p, 'uPointer'),
      this.particles.point.x,
      this.particles.point.y
    );
    gl.uniform1f(gl.getUniformLocation(p, 'uTime'), this.time);
    gl.uniform1f(gl.getUniformLocation(p, 'uIntensity'), this.intensity);
    gl.uniform1f(gl.getUniformLocation(p, 'uBloom'), this.variant.includes('bloom') ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(p, 'uDark'), this.variant.endsWith('dark') ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(p, 'uGesture'), this.particles.energy);
    gl.uniform1f(gl.getUniformLocation(p, 'uHold'), this.particles.holdSeconds);
    blitTo(gl, null);
    if (this.particles.count === 0) return;
    const points = this.particleProgram;
    gl.useProgram(points);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.particles.vertexData());
    const particleLoc = gl.getAttribLocation(points, 'aParticle'),
      lifeLoc = gl.getAttribLocation(points, 'aLife');
    gl.enableVertexAttribArray(particleLoc);
    gl.vertexAttribPointer(particleLoc, 4, gl.FLOAT, false, 32, 0);
    gl.enableVertexAttribArray(lifeLoc);
    gl.vertexAttribPointer(lifeLoc, 4, gl.FLOAT, false, 32, 16);
    gl.uniform1f(gl.getUniformLocation(points, 'uCanvas'), gl.canvas.width);
    gl.uniform1f(gl.getUniformLocation(points, 'uMaxPoint'), this.maxPoint);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArrays(gl.POINTS, 0, this.particles.count);
    gl.disable(gl.BLEND);
    gl.disableVertexAttribArray(particleLoc);
    gl.disableVertexAttribArray(lifeLoc);
  }

  dispose(): void {
    disposeSimDoubleTarget(this.gl, this.state);
    this.gl.deleteBuffer(this.quad);
    this.gl.deleteBuffer(this.particleBuffer);
  }
}
